// src/pages/Chat.js
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  TextField,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  Container,
  Avatar,
  Badge,
  InputAdornment,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import ReplyIcon from "@mui/icons-material/Reply";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  getChatPeers,
  getChatMessages,
  sendChatMessage,
  uploadChatMedia,
} from "../api/axios";
import EmojiPicker from "emoji-picker-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Chat() {
  const [peers, setPeers] = useState([]);
  const [selectedPeer, setSelectedPeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [loadingPeers, setLoadingPeers] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [error, setError] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({});
  const chatContainerRef = useRef(null);
  const bottomRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPeers();
  }, []);

  const getFileType = (url) => {
    const ext = url.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png"].includes(ext)) return "image";
    if (["mp4", "3gp"].includes(ext)) return "video";
    if (["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext))
      return "document";
    return "unknown";
  };

  const fetchPeers = async () => {
    setLoadingPeers(true);
    try {
      const res = await getChatPeers();
      setPeers(
        res.data.sort(
          (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)
        )
      );
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to load peers.");
    } finally {
      setLoadingPeers(false);
    }
  };

  useEffect(() => {
    if (!selectedPeer) return;
    loadMessages(selectedPeer.peer_number, true);
  }, [selectedPeer]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await getChatPeers();
        const updatedPeers = res.data.sort(
          (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)
        );
        const newUnreadCounts = { ...unreadCounts };
        updatedPeers.forEach((peer) => {
          if (
            !peers.find((p) => p.peer_number === peer.peer_number) ||
            new Date(peer.last_message_at) >
              new Date(
                peers.find((p) => p.peer_number === peer.peer_number)
                  ?.last_message_at || 0
              )
          ) {
            if (selectedPeer?.peer_number !== peer.peer_number) {
              newUnreadCounts[peer.peer_number] =
                (newUnreadCounts[peer.peer_number] || 0) + 1;
            } else {
              loadMessages(peer.peer_number, false);
            }
          }
        });
        setUnreadCounts(newUnreadCounts);
        setPeers(updatedPeers);
      } catch (err) {
        console.error(err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [peers, selectedPeer, unreadCounts]);

  const loadMessages = async (peerNumber, scrollToBottom) => {
    setLoadingMsgs(true);
    try {
      const res = await getChatMessages(peerNumber);
      setMessages(res.data);
      if (scrollToBottom) {
        setTimeout(
          () => bottomRef.current?.scrollIntoView({ behavior: "auto" }),
          100
        );
      }
      setUnreadCounts((prev) => ({ ...prev, [peerNumber]: 0 }));
    } catch (err) {
      console.error(err);
      setError("Failed to load messages.");
    } finally {
      setLoadingMsgs(false);
    }
  };

  const handleSend = async () => {
    if (uploading) {
      toast.warning("Please wait, media is still uploading.");
      return;
    }
    if (!newMsg.trim() && !mediaUrl) return;
    if (!selectedPeer) return;

    // ✅ Detect media_type from uploaded file (if any)
    let mediaType = null;
    if (mediaUrl) {
      const ext = mediaUrl.split(".").pop().toLowerCase();
      if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
        mediaType = "image";
      else if (["mp4", "mov", "3gp"].includes(ext)) mediaType = "video";
      else if (
        ["pdf", "doc", "docx", "xlsx", "xls", "ppt", "pptx"].includes(ext)
      )
        mediaType = "document";
    }

    try {
      const payload = {
        to_number: selectedPeer.peer_number,
        message: newMsg.trim(),
        media_url: mediaUrl || null,
        media_type: mediaType, // ✅ send this only if media is present
        reply_to_id: replyTo?.id || null,
      };

      console.log("📤 Sending payload:", payload);

      await sendChatMessage(payload);

      setNewMsg("");
      setMediaUrl("");
      setMediaPreview(null);
      setSelectedFile(null);
      setReplyTo(null);
      setShowEmoji(false);
      await loadMessages(selectedPeer.peer_number, true);
    } catch (err) {
      console.error("❌ Error sending message:", err);
      toast.error("Failed to send message.");
    }
  };

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{ mt: 0, height: "85vh", width: "100%", ml: 0, mr: 0 }}
    >
      <Paper
        sx={{
          m: 0,
          display: "flex",
          height: "100%",
          borderRadius: 2,
          overflow: "hidden",
          flex: 1,
        }}
        elevation={3}
      >
        {/* Sidebar */}
        <Box
          sx={{
            width: 300,
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflowY: "auto",
            bgcolor: "#f9f9f9",
          }}
        >
          {loadingPeers ? (
            <Box p={2} textAlign="center">
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List disablePadding>
              {peers.map((peer) => (
                <ListItemButton
                  key={peer.peer_number}
                  selected={selectedPeer?.peer_number === peer.peer_number}
                  onClick={() => setSelectedPeer(peer)}
                  sx={{
                    "&.Mui-selected": { bgcolor: "#e0f7fa" },
                    "&:hover": { bgcolor: "#f1f1f1" },
                  }}
                >
                  <Badge
                    badgeContent={unreadCounts[peer.peer_number]}
                    color="primary"
                    overlap="circular"
                    sx={{ mr: 1 }}
                  >
                    <Avatar>
                      {peer.peer_name?.charAt(0) || peer.peer_number[0]}
                    </Avatar>
                  </Badge>
                  <ListItemText
                    primary={
                      <Typography fontWeight="bold">
                        {peer.peer_name || peer.peer_number}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {/* {new Date(peer.last_message_at).toLocaleString()} */}
                        {peer.peer_number }
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        {/* Chat */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
            <Typography variant="h6">
              {selectedPeer
                ? selectedPeer.peer_name || selectedPeer.peer_number
                : "Select a conversation"}
            </Typography>
          </Box>

          {/* {mediaUrl && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "#f0f0f0",
                p: 1,
                borderRadius: 1,
                mb: 1,
              }}
            >
              {(() => {
                const type = getFileType(mediaUrl);
                if (type === "image") {
                  return (
                    <img
                      src={mediaUrl}
                      alt="preview"
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                  );
                } else if (type === "video") {
                  return (
                    <video
                      src={mediaUrl}
                      controls
                      style={{ width: 120, height: 100, borderRadius: 4 }}
                    />
                  );
                } else if (type === "document") {
                  return (
                    <Box>
                      <Typography fontSize={14}>
                        📄 {mediaUrl.split("/").pop()}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()}
              <IconButton size="small" onClick={() => setMediaUrl("")}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )} */}

          {/* Messages container */}
          <Box
            ref={chatContainerRef}
            sx={{
              flex: 1,
              p: 2,
              overflowY: "auto",
              backgroundColor: "#eef2f5",
            }}
          >
            {loadingMsgs ? (
              <Box textAlign="center">
                <CircularProgress size={24} />
              </Box>
            ) : selectedPeer ? (
              messages.map((msg) => (
                <Box
                  key={msg.id}
                  sx={{
                    mb: 1,
                    display: "flex",
                    justifyContent:
                      msg.direction === "out" ? "flex-end" : "flex-start",
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      maxWidth: "70%",
                      bgcolor: msg.direction === "out" ? "#dcf8c6" : "white",
                      borderRadius: 2,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    }}
                  >
                    {msg.reply_message && (
                      <Box
                        sx={{
                          borderLeft: "2px solid #ccc",
                          pl: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Reply: {msg.reply_message}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="body2">{msg.message}</Typography>
                    {msg.media_url &&
                      (() => {
                        const type = getFileType(msg.media_url);
                        if (type === "image") {
                          return (
                            <img
                              src={
                                msg.media_url.startsWith("http")
                                  ? msg.media_url
                                  : `https://waapi.gensparc.com${msg.media_url}`
                              }
                              alt="media"
                              style={{
                                marginTop: 5,
                                maxWidth: "100%",
                                borderRadius: 4,
                              }}
                            />
                          );
                        } else if (type === "video") {
                          return (
                            <video
                              src={
                                msg.media_url.startsWith("http")
                                  ? msg.media_url
                                  : `https://waapi.gensparc.com${msg.media_url}`
                              }
                              controls
                              style={{
                                marginTop: 5,
                                maxWidth: "100%",
                                borderRadius: 4,
                              }}
                            />
                          );
                        } else if (type === "document") {
                          return (
                            <Box sx={{ mt: 1 }}>
                              <Typography fontSize={14}>
                                📄{" "}
                                <a
                                  src={
                                    msg.media_url.startsWith("http")
                                      ? msg.media_url
                                      : `https://waapi.gensparc.com${msg.media_url}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {msg.media_url.split("/").pop()}
                                </a>
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })()}

                    <Typography
                      variant="caption"
                      sx={{ display: "block", textAlign: "right", mt: 0.5 }}
                    >
                      {new Date(msg.created_at).toLocaleTimeString()}
                    </Typography>
                    {msg.direction === "in" && (
                      <IconButton size="small" onClick={() => setReplyTo(msg)}>
                        <ReplyIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Paper>
                </Box>
              ))
            ) : (
              <Typography color="text.secondary">
                No conversation selected
              </Typography>
            )}
            <div ref={bottomRef} />
          </Box>

          {/* Message input */}
          {selectedPeer && (
            <>
              {replyTo && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "#f0f0f0",
                    px: 1,
                    py: 0.5,
                    borderLeft: "4px solid #007bff",
                  }}
                >
                  <Box>
                    <Typography fontSize={12} fontWeight="bold">
                      Replying to
                    </Typography>
                    <Typography fontSize={14}>{replyTo.message}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setReplyTo(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* {mediaPreview && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "#f0f0f0",
                    p: 1,
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  {(() => {
                    const type = getFileType(mediaPreview);
                    if (type === "image") {
                      return (
                        <img
                          src={mediaPreview}
                          alt="preview"
                          style={{
                            width: 100,
                            height: 100,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                        />
                      );
                    } else if (type === "video") {
                      return (
                        <video
                          src={mediaPreview}
                          controls
                          style={{ width: 120, height: 100, borderRadius: 4 }}
                        />
                      );
                    } else {
                      return (
                        <Box>
                          <Typography fontSize={14}>
                            📄 {selectedFile?.name || "Document"}
                          </Typography>
                        </Box>
                      );
                    }
                  })()}

                  <IconButton
                    size="small"
                    onClick={() => {
                      setMediaPreview(null);
                      setMediaUrl("");
                      setSelectedFile(null);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )} */}

              <Divider />
              {mediaUrl && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end", // Align the whole preview to right
                    mb: 1,
                    bgcolor: "#f0f0f0",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      bgcolor: "#f0f0f0",
                      borderRadius: 1,
                      p: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: 160,
                      height: 130,
                    }}
                  >
                    {/* Close (X) Button */}
                    <IconButton
                      size="small"
                      onClick={() => setMediaUrl("")}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        backgroundColor: "#fff",
                        "&:hover": {
                          backgroundColor: "#e0e0e0",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>

                    {/* Media Preview */}
                    {(() => {
                      const type = getFileType(mediaUrl);
                      if (type === "image") {
                        return (
                          <img
                            src={mediaUrl}
                            alt="preview"
                            style={{
                              width: 120,
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 4,
                            }}
                          />
                        );
                      } else if (type === "video") {
                        return (
                          <video
                            src={mediaUrl}
                            controls
                            style={{ width: 140, height: 100, borderRadius: 4 }}
                          />
                        );
                      } else if (type === "document") {
                        return (
                          <Typography fontSize={14} textAlign="center">
                            📄 {mediaUrl.split("/").pop()}
                          </Typography>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  p: 1,
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-end",
                  bgcolor: "white",
                }}
              >
                <IconButton onClick={() => setShowEmoji(!showEmoji)}>
                  <InsertEmoticonIcon />
                </IconButton>

                {showEmoji && (
                  <Box position="absolute" bottom={70} zIndex={10}>
                    <Box display="flex" justifyContent="flex-end" pr={1}>
                      <IconButton
                        size="small"
                        onClick={() => setShowEmoji(false)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        console.log("🧪 Selected emoji object:", emojiData);
                        setNewMsg((prev) => prev + emojiData.emoji);
                      }}
                    />
                  </Box>
                )}

                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Type your message…"
                  value={newMsg}
                  onChange={(e) => setNewMsg(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                />

                {/* File upload input */}
                <IconButton component="label">
                  <AttachFileIcon />
                  <input
                    type="file"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      toast.info("Uploading media...");
                      setSelectedFile(file); // Save the file
                      const previewUrl = URL.createObjectURL(file);
                      setMediaPreview(previewUrl);
                      try {
                        setUploading(true);
                        const res = await uploadChatMedia(file);
                        console.log("📎 Full upload response:", res);
                        console.log("📎 Response data:", res.data);
                        console.log("📎 media_url:", res?.data?.url);
                        setMediaUrl(res.data.url);
                        console.log(
                          "📎 Media uploaded. URL set to:",
                          res.data.url
                        );
                        toast.success("Media uploaded successfully!");
                      } catch (err) {
                        console.error("Upload error:", err);
                        setMediaPreview(null);
                        setSelectedFile(null);
                        toast.error("Failed to upload media.");
                      } finally {
                        setUploading(false);
                      }
                    }}
                  />
                </IconButton>

                <IconButton color="primary" onClick={handleSend}>
                  <SendIcon />
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      </Paper>
      <ToastContainer position="top-right" autoClose={3000} />
    </Container>
  );
}
